import type { ConnectionsBhSankeyNode } from './connectionsBehavioralCareSnapshot.util';

/** Sankey link between two behavioral-health connection nodes. */
export interface ConnectionsBhSankeyLink {
  source: string;
  target: string;
  value: number;
  color: string;
}

/** Link colors for the BH connections Sankey (Studio design). */
export const BH_SANKEY_LINK_COLORS = {
  connectedToInNetwork: '#614189',
  connectedToReema: '#81CDC3',
  reemaToCurrently: '#158C7C',
  reemaToNoLonger: '#A1D9D2',
} as const;

const nodeValueById = (
  nodes: ConnectionsBhSankeyNode[],
  id: string,
): number => {
  return nodes.find(node => node.id === id)?.value ?? 0;
};

/**
 * Partitions a total across weights using largest-remainder rounding.
 * @param total - Amount to partition.
 * @param weights - Relative weights (need not sum to total).
 * @returns Integer flows summing to `total`.
 */
export const partitionSankeyFlow = (
  total: number,
  weights: number[],
): number[] => {
  if (total <= 0 || weights.length === 0) {
    return weights.map(() => 0);
  }

  const weightSum = weights.reduce((sum, weight) => sum + Math.max(0, weight), 0);
  if (weightSum <= 0) {
    return weights.map(() => 0);
  }

  const raw = weights.map(weight =>
    Math.max(0, weight) > 0 ? (total * Math.max(0, weight)) / weightSum : 0,
  );
  const floors = raw.map(value => Math.floor(value));
  let remainder = total - floors.reduce((sum, value) => sum + value, 0);

  const order = raw
    .map((value, index) => ({ index, fraction: value - floors[index] }))
    .sort((left, right) => right.fraction - left.fraction);

  const flows = [...floors];
  for (const entry of order) {
    if (remainder <= 0) {
      break;
    }
    flows[entry.index] += 1;
    remainder -= 1;
  }

  return flows;
};

/**
 * Builds Sankey link flows from snapshot nodes.
 * Labels use raw warehouse counts; link widths partition overlapping cohorts.
 * @param nodes - Five-node BH connections Sankey snapshot array.
 * @returns Four links for the Studio Sankey layout.
 */
export const buildConnectionsBhSankeyLinks = (
  nodes: ConnectionsBhSankeyNode[],
): ConnectionsBhSankeyLink[] => {
  const connected = nodeValueById(nodes, 'connected');
  const inNetwork = nodeValueById(nodes, 'inNetwork');
  const reema = nodeValueById(nodes, 'reema');
  const currently = nodeValueById(nodes, 'currently');
  const noLonger = nodeValueById(nodes, 'noLonger');

  if (connected <= 0) {
    return [];
  }

  const [inNetworkFlow, reemaFlow] = partitionSankeyFlow(connected, [
    inNetwork,
    reema,
  ]);
  const [currentlyFlow, noLongerFlow] = partitionSankeyFlow(reemaFlow, [
    currently,
    noLonger,
  ]);

  return [
    {
      source: 'connected',
      target: 'inNetwork',
      value: inNetworkFlow,
      color: BH_SANKEY_LINK_COLORS.connectedToInNetwork,
    },
    {
      source: 'connected',
      target: 'reema',
      value: reemaFlow,
      color: BH_SANKEY_LINK_COLORS.connectedToReema,
    },
    {
      source: 'reema',
      target: 'currently',
      value: currentlyFlow,
      color: BH_SANKEY_LINK_COLORS.reemaToCurrently,
    },
    {
      source: 'reema',
      target: 'noLonger',
      value: noLongerFlow,
      color: BH_SANKEY_LINK_COLORS.reemaToNoLonger,
    },
  ];
};
